"use client";

import { LayoutGrid, List, Search, Upload, X } from "lucide-react";
import { Button } from "@repo/ui";
import { Input } from "@repo/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui";
import {
  MEDIA_KIND_LABELS,
  MEDIA_SORT_LABELS,
  isFilterActive,
  type MediaKind,
  type MediaLibraryFilter,
  type MediaSort,
  type UsedByFilter,
} from "@/lib/media-library-filter";
import type { MediaUsageRelation } from "@/services/pylo-media.service";
import { UsedByFilterPopover } from "./used-by-filter";
import { MediaFacetSelects } from "./media-facet-selects";
import { formatFileSize } from "./media-utils";

export type MediaViewMode = "grid" | "list";

interface MediaToolbarProps {
  filter: MediaLibraryFilter;
  relations: Array<MediaUsageRelation>;
  search: string;
  view: MediaViewMode;
  summary: { count: number; bytes: number };
  /** Folds the facet rail into this toolbar and drops the byte total. */
  compact?: boolean;
  counts: Record<MediaKind, number>;
  onSearchChange: (value: string) => void;
  onSelectKind: (kind?: MediaKind) => void;
  onSetUsedBy: (usedBy?: UsedByFilter) => void;
  onSetSort: (sort: MediaSort) => void;
  onSetView: (view: MediaViewMode) => void;
  onReset: () => void;
  onUpload?: () => void;
}

const Chip = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
  <span className="inline-flex items-center gap-1 rounded-full border bg-white py-0.5 pl-2.5 pr-1 text-[12px]">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="rounded-full p-0.5 text-muted-foreground hover:bg-gray-100 hover:text-foreground"
      aria-label={`Remove ${label} filter`}
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

export const MediaToolbar = ({
  filter,
  relations,
  search,
  view,
  summary,
  compact = false,
  counts,
  onSearchChange,
  onSelectKind,
  onSetUsedBy,
  onSetSort,
  onSetView,
  onReset,
  onUpload,
}: MediaToolbarProps) => {
  const usedByEntity = filter.usedBy?.entityName;

  return (
    <div className="flex flex-col gap-3 border-b bg-white px-4 py-3">
      <div className="flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search file names…"
            className="h-8 pl-8 text-[13px]"
          />
        </div>

        {compact && (
          <MediaFacetSelects filter={filter} counts={counts} onSelectKind={onSelectKind} />
        )}

        <UsedByFilterPopover relations={relations} onSelect={(usedBy) => onSetUsedBy(usedBy)} />

        <div className="ml-auto flex items-center gap-2">
          {onUpload && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-[13px]"
              onClick={onUpload}
            >
              <Upload className="h-3.5 w-3.5" />
              Upload
            </Button>
          )}

          <Select value={filter.sort} onValueChange={(value) => onSetSort(value as MediaSort)}>
            <SelectTrigger className="h-8 w-[150px] text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(MEDIA_SORT_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value} className="text-[13px]">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center rounded-md border p-0.5">
            <Button
              variant={view === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onSetView("grid")}
              aria-label="Grid view"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant={view === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onSetView("list")}
              aria-label="List view"
            >
              <List className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[12px] tabular-nums text-muted-foreground">
          {summary.count.toLocaleString()} files
          {!compact && ` · ${formatFileSize(summary.bytes)}`}
        </span>

        {filter.kind && (
          <Chip label={MEDIA_KIND_LABELS[filter.kind]} onRemove={() => onSelectKind(undefined)} />
        )}

        {filter.usedBy && (
          <Chip
            label={`Used by ${usedByEntity ?? filter.usedBy.fieldName}${
              filter.usedBy.instanceLabel ? `: ${filter.usedBy.instanceLabel}` : ""
            }`}
            onRemove={() => onSetUsedBy(undefined)}
          />
        )}

        {isFilterActive(filter) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[12px] text-muted-foreground"
            onClick={onReset}
          >
            Clear all
          </Button>
        )}
      </div>
    </div>
  );
};
